import { type FC, useMemo } from 'react';

import {
    Tune as TuneIcon,
    Settings as SettingsIcon,
    Book as BookIcon,
    Lock as LockIcon,
    Login as LoginIcon,
    People as PeopleIcon,
    Layers as LayersIcon,
    VpnKey as VpnKeyIcon,
    Wifi as WifiIcon,
    Storage as StorageIcon,
    SupervisorAccount as SupervisorAccountIcon,
    VideoSettings as VideoSettingsIcon,
    School as ClassIcon
} from '@mui/icons-material';
import { Box, List, ListItemButton, ListItemText, ListItemIcon, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import AdminSettingsContainer from './AdminSettingsContainer';
import { AdminTabs } from './config/adminSettingsConfig';
import AdminUserState from '../../components/common/userState/AdminUserState';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import classLogoFull from '../../images/class-logo.svg';

const Sidebar = styled(Box)`
    width: 240px;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-right: 1px solid ${(props) => props.theme.palette.divider};
    background-color: ${(props) => props.theme.palette.background.paper};
`;

const LogoBox = styled(Box)`
    display: flex;
    align-items: center;
    padding: ${(props) => props.theme.spacing(2)};
    gap: ${(props) => props.theme.spacing(1)};
`;

const LogoImage = styled('img')`
    width: 20px;
    height: 20px;
    object-fit: contain;
`;

const HeadingText = styled(Typography)`
    font-weight: 600;
    font-size: 20px;
    white-space: nowrap;
`;

const MenuList = styled(List)`
    flex-grow: 1;
`;

const StyledListItemButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>`
    flex: 1;
    justify-content: flex-start;
    align-items: center;
    max-height: 48px;
    overflow: hidden;

    ${(props) =>
        props.selected &&
        `
        color: ${props.theme.palette.primary.main};
        border-right: 4px solid ${props.theme.palette.primary.main};
        background-color: ${props.theme.palette.action.selected};
        
        & .MuiListItemIcon-root {
            color: ${props.theme.palette.primary.main};
        }
    `}
`;

const ContentBox = styled(Box)`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;
`;

const ScrollableMenuList = styled(MenuList)`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

type MenuItem = {
    label: string;
    icon: JSX.Element;
    key: AdminTabs;
};

const LeftMenuNav: FC = () => {
    const { t } = useTranslation('admin_settings', { useSuspense: false });
    const { activeTab, school, setActiveTab, showOrgType } = useContainer(AdminSettingsContainer);
    const { isAdmin, canManageClasses, isSuperAdmin, user } = useContainer(AdminAuthContainer);
    const history = useHistory();
    const { path } = useRouteMatch();

    const { data: orgSettings } = useOrgSettings(true);
    const name = user?.user_name || '';
    const email = user?.email || '';

    const handleChange = (value) => {
        setActiveTab(value);
        history.push(`${path}${value}`);
    };

    const menuItems: MenuItem[] = useMemo(() => {
        const items: MenuItem[] = [];

        if (showOrgType && isAdmin) {
            items.push({
                label: t('navigationTabs.general'),
                icon: <TuneIcon />,
                key: AdminTabs.General
            });
        }
        if (isAdmin) {
            items.push({
                label: t('navigationTabs.configuration'),
                icon: <SettingsIcon />,
                key: AdminTabs.Features
            });
        }

        if (isAdmin) {
            items.push({
                label: t('navigationTabs.terminology'),
                icon: <BookIcon />,
                key: AdminTabs.Terminology
            });
        }

        if (isAdmin) {
            items.push({
                label: t('navigationTabs.auth'),
                icon: <LockIcon />,
                key: AdminTabs.Auth
            });
        }

        if (isAdmin && orgSettings?.featureSettings.options.hasSSOFeature) {
            items.push({
                label: t('navigationTabs.sso'),
                icon: <LoginIcon />,
                key: AdminTabs.Sso
            });
        }

        if (canManageClasses) {
            items.push({
                label: t('navigationTabs.classes'),
                icon: <ClassIcon />,
                key: AdminTabs.Classes
            });
        }

        if (isAdmin) {
            items.push({
                label: t('navigationTabs.users'),
                icon: <PeopleIcon />,
                key: AdminTabs.Users
            });
        }

        if (isAdmin) {
            items.push({
                label: t('navigationTabs.integrations'),
                icon: <LayersIcon />,
                key: AdminTabs.Integrations
            });
        }

        if (isAdmin) {
            items.push({
                label: t('navigationTabs.apiKeys'),
                icon: <VpnKeyIcon />,
                key: AdminTabs.ApiKeys
            });
        }

        if (isAdmin) {
            items.push({
                label: t('navigationTabs.networking'),
                icon: <WifiIcon />,
                key: AdminTabs.Networking
            });
        }

        if (isAdmin) {
            items.push({
                label: t('navigationTabs.recordings'),
                icon: <StorageIcon />,
                key: AdminTabs.RecordingsTab
            });
        }

        if (isSuperAdmin) {
            items.push({
                label: t('navigationTabs.super-admin'),
                icon: <SupervisorAccountIcon />,
                key: AdminTabs.SuperAdmin
            });

            if (!school?.isManagedZoom && !school?.isTeams) {
                items.push({
                    label: t('navigationTabs.zoomSettings'),
                    icon: <VideoSettingsIcon />,
                    key: AdminTabs.ZoomSettings
                });
            }
        }

        return items;
    }, [showOrgType, orgSettings, isSuperAdmin, school, t, isAdmin, canManageClasses]);

    return (
        <Sidebar>
            <ContentBox>
                <LogoBox>
                    <LogoImage src={classLogoFull} alt="class logo" />
                    <HeadingText variant="h1">{t('navigationTabs.title')}</HeadingText>
                </LogoBox>
                <ScrollableMenuList>
                    {menuItems.map((item) => (
                        <StyledListItemButton
                            key={item.key}
                            selected={activeTab === item.key}
                            onClick={() => handleChange(item.key)}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} />
                        </StyledListItemButton>
                    ))}
                </ScrollableMenuList>
            </ContentBox>
            <AdminUserState name={name} email={email} />
        </Sidebar>
    );
};

export default LeftMenuNav;
// This code defines a sidebar navigation component for an admin settings page.
